import React, { useState, useEffect } from 'react';
import {
  ExternalLink,
  Check,
  Mail,
  Globe,
  Copy,
  Info,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { useAuth } from '../../../app/providers/AuthProvider';

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: string;
  connected: boolean;
  category: 'productivity' | 'communication' | 'automation';
}

interface EmailSettings {
  use_platform_default: boolean;
  from_name: string;
  from_email: string;
  reply_to: string;
  provider: 'smtp' | 'sendgrid' | 'mailgun';
  smtp_host: string;
  smtp_port: number;
  smtp_encryption: string;
  smtp_username: string;
  smtp_password: string;
  sendgrid_api_key: string;
  mailgun_domain: string;
  mailgun_api_key: string;
}

interface DomainSettings {
  subdomain: string;
  custom_domain: string;
  custom_domain_verified: boolean;
  platform_domain: string;
  platform_ip: string;
}

const integrations: Integration[] = [
  {
    id: 'google-sheets',
    name: 'Google Sheets',
    description: 'Send submissions to a sheet',
    icon: 'https://www.gstatic.com/images/branding/product/2x/sheets_2020q4_48dp.png',
    connected: false,
    category: 'productivity',
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    description: 'Sync events and schedules',
    icon: 'https://www.gstatic.com/images/branding/product/2x/calendar_2020q4_48dp.png',
    connected: true,
    category: 'productivity',
  },
  {
    id: 'google-drive',
    name: 'Google Drive',
    description: 'Store and share files',
    icon: 'https://www.gstatic.com/images/branding/product/2x/drive_2020q4_48dp.png',
    connected: false,
    category: 'productivity',
  },
  {
    id: 'notion',
    name: 'Notion',
    description: 'Connect your workspace',
    icon: 'https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png',
    connected: false,
    category: 'productivity',
  },
  {
    id: 'airtable',
    name: 'Airtable',
    description: 'Database and spreadsheet hybrid',
    icon: 'https://seeklogo.com/images/A/airtable-logo-216B9AF035-seeklogo.com.png',
    connected: false,
    category: 'productivity',
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Team communication',
    icon: 'https://cdn.brandfetch.io/idR3duQxYt/theme/dark/symbol.svg',
    connected: false,
    category: 'communication',
  },
  {
    id: 'zapier',
    name: 'Zapier',
    description: 'Automate workflows',
    icon: 'https://cdn.brandfetch.io/idsBiOpZ8g/w/512/h/512/theme/dark/icon.png',
    connected: false,
    category: 'automation',
  },
  {
    id: 'webhooks',
    name: 'Webhooks',
    description: 'Custom HTTP callbacks',
    icon: '',
    connected: false,
    category: 'automation',
  },
];

const EMAIL_PROVIDERS = [
  { id: 'smtp', name: 'SMTP Server', description: 'Connect to any SMTP server' },
  { id: 'sendgrid', name: 'SendGrid', description: 'Twilio SendGrid' },
  { id: 'mailgun', name: 'Mailgun', description: 'Mailgun email service' },
];

export function IntegrationsSection() {
  const { currentSchool } = useAuth();
  const [activeTab, setActiveTab] = useState<'integrations' | 'email' | 'domain'>('integrations');
  const [connectedIntegrations, setConnectedIntegrations] = useState<string[]>(
    integrations.filter((i) => i.connected).map((i) => i.id)
  );
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [verifyingDomain, setVerifyingDomain] = useState(false);

  // Email settings
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    use_platform_default: true,
    from_name: currentSchool?.name || '',
    from_email: '',
    reply_to: '',
    provider: 'smtp',
    smtp_host: '',
    smtp_port: 587,
    smtp_encryption: 'tls',
    smtp_username: '',
    smtp_password: '',
    sendgrid_api_key: '',
    mailgun_domain: '',
    mailgun_api_key: '',
  });

  // Domain settings
  const [domainSettings, setDomainSettings] = useState<DomainSettings>({
    subdomain: currentSchool?.name?.toLowerCase().replace(/\s+/g, '-') || '',
    custom_domain: '',
    custom_domain_verified: false,
    platform_domain: 'viscool.io',
    platform_ip: '203.0.113.50',
  });

  const handleConnect = (id: string) => {
    if (connectedIntegrations.includes(id)) {
      setConnectedIntegrations((prev) => prev.filter((i) => i !== id));
    } else {
      setConnectedIntegrations((prev) => [...prev, id]);
    }
  };

  const handleSaveEmail = async () => {
    setSaving(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save email settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDomain = async () => {
    setSaving(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save domain settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    setVerifyingDomain(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setDomainSettings((prev) => ({ ...prev, custom_domain_verified: true }));
    } catch (error) {
      console.error('Failed to verify domain:', error);
    } finally {
      setVerifyingDomain(false);
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const groupedIntegrations = {
    productivity: integrations.filter((i) => i.category === 'productivity'),
    communication: integrations.filter((i) => i.category === 'communication'),
    automation: integrations.filter((i) => i.category === 'automation'),
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('integrations')}
          className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'integrations'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <ExternalLink size={18} />
          Integrations
        </button>
        <button
          onClick={() => setActiveTab('email')}
          className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'email'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Mail size={18} />
          Email Settings
        </button>
        <button
          onClick={() => setActiveTab('domain')}
          className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'domain'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Globe size={18} />
          Domain
        </button>
      </div>

      <div className="p-6">
        {/* Integrations Tab */}
        {activeTab === 'integrations' && (
          <>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Discover Integrations</h2>
            <p className="text-sm text-gray-500 mb-6">
              Connect your favorite tools to streamline your workflow.
            </p>

            {/* Productivity */}
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Productivity</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupedIntegrations.productivity.map((integration) => (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    isConnected={connectedIntegrations.includes(integration.id)}
                    onConnect={() => handleConnect(integration.id)}
                  />
                ))}
              </div>
            </div>

            {/* Communication */}
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Communication</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupedIntegrations.communication.map((integration) => (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    isConnected={connectedIntegrations.includes(integration.id)}
                    onConnect={() => handleConnect(integration.id)}
                  />
                ))}
              </div>
            </div>

            {/* Automation */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-4">Automation</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupedIntegrations.automation.map((integration) => (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    isConnected={connectedIntegrations.includes(integration.id)}
                    onConnect={() => handleConnect(integration.id)}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {/* Email Settings Tab */}
        {activeTab === 'email' && (
          <div className="max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Email Settings</h2>
            <p className="text-sm text-gray-500 mb-6">
              Configure how emails are sent from your school.
            </p>

            {/* Use Platform Default Toggle */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Use Platform Default</p>
                  <p className="text-sm text-gray-500">
                    Use the platform's email configuration for sending emails
                  </p>
                </div>
                <button
                  onClick={() =>
                    setEmailSettings((prev) => ({
                      ...prev,
                      use_platform_default: !prev.use_platform_default,
                    }))
                  }
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    emailSettings.use_platform_default ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      emailSettings.use_platform_default ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {!emailSettings.use_platform_default && (
              <>
                {/* Sender Information */}
                <div className="space-y-4 mb-6">
                  <h3 className="text-sm font-medium text-gray-700">Sender Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        From Name
                      </label>
                      <input
                        type="text"
                        value={emailSettings.from_name}
                        onChange={(e) =>
                          setEmailSettings((prev) => ({ ...prev, from_name: e.target.value }))
                        }
                        placeholder="Your School Name"
                        className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        From Email
                      </label>
                      <input
                        type="email"
                        value={emailSettings.from_email}
                        onChange={(e) =>
                          setEmailSettings((prev) => ({ ...prev, from_email: e.target.value }))
                        }
                        placeholder="noreply@yourschool.com"
                        className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reply-To Email
                    </label>
                    <input
                      type="email"
                      value={emailSettings.reply_to}
                      onChange={(e) =>
                        setEmailSettings((prev) => ({ ...prev, reply_to: e.target.value }))
                      }
                      placeholder="support@yourschool.com"
                      className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                </div>

                {/* Email Provider */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Email Provider</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {EMAIL_PROVIDERS.map((provider) => (
                      <button
                        key={provider.id}
                        onClick={() =>
                          setEmailSettings((prev) => ({
                            ...prev,
                            provider: provider.id as EmailSettings['provider'],
                          }))
                        }
                        className={`p-3 rounded-lg border-2 text-left transition-colors ${
                          emailSettings.provider === provider.id
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              emailSettings.provider === provider.id
                                ? 'border-teal-500 bg-teal-500'
                                : 'border-gray-300'
                            }`}
                          >
                            {emailSettings.provider === provider.id && (
                              <Check size={10} className="text-white" />
                            )}
                          </div>
                          <span className="font-medium text-gray-900 text-sm">{provider.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* SMTP Configuration */}
                {emailSettings.provider === 'smtp' && (
                  <div className="space-y-4 mb-6">
                    <h3 className="text-sm font-medium text-gray-700">SMTP Configuration</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          SMTP Host
                        </label>
                        <input
                          type="text"
                          value={emailSettings.smtp_host}
                          onChange={(e) =>
                            setEmailSettings((prev) => ({ ...prev, smtp_host: e.target.value }))
                          }
                          placeholder="smtp.gmail.com"
                          className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          SMTP Port
                        </label>
                        <input
                          type="number"
                          value={emailSettings.smtp_port}
                          onChange={(e) =>
                            setEmailSettings((prev) => ({
                              ...prev,
                              smtp_port: Number(e.target.value),
                            }))
                          }
                          className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Encryption
                      </label>
                      <select
                        value={emailSettings.smtp_encryption}
                        onChange={(e) =>
                          setEmailSettings((prev) => ({ ...prev, smtp_encryption: e.target.value }))
                        }
                        className="w-full h-10 px-4 border border-gray-300 rounded-lg bg-white"
                      >
                        <option value="tls">TLS</option>
                        <option value="ssl">SSL</option>
                        <option value="none">None</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Username
                        </label>
                        <input
                          type="text"
                          value={emailSettings.smtp_username}
                          onChange={(e) =>
                            setEmailSettings((prev) => ({ ...prev, smtp_username: e.target.value }))
                          }
                          className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword['smtp'] ? 'text' : 'password'}
                            value={emailSettings.smtp_password}
                            onChange={(e) =>
                              setEmailSettings((prev) => ({
                                ...prev,
                                smtp_password: e.target.value,
                              }))
                            }
                            className="w-full h-10 px-4 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('smtp')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword['smtp'] ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* SendGrid Configuration */}
                {emailSettings.provider === 'sendgrid' && (
                  <div className="space-y-4 mb-6">
                    <h3 className="text-sm font-medium text-gray-700">SendGrid Configuration</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword['sendgrid'] ? 'text' : 'password'}
                          value={emailSettings.sendgrid_api_key}
                          onChange={(e) =>
                            setEmailSettings((prev) => ({
                              ...prev,
                              sendgrid_api_key: e.target.value,
                            }))
                          }
                          placeholder="SG.xxxxxxxxxxxx"
                          className="w-full h-10 px-4 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('sendgrid')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword['sendgrid'] ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mailgun Configuration */}
                {emailSettings.provider === 'mailgun' && (
                  <div className="space-y-4 mb-6">
                    <h3 className="text-sm font-medium text-gray-700">Mailgun Configuration</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Domain
                        </label>
                        <input
                          type="text"
                          value={emailSettings.mailgun_domain}
                          onChange={(e) =>
                            setEmailSettings((prev) => ({
                              ...prev,
                              mailgun_domain: e.target.value,
                            }))
                          }
                          placeholder="mg.yourdomain.com"
                          className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          API Key
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword['mailgun'] ? 'text' : 'password'}
                            value={emailSettings.mailgun_api_key}
                            onChange={(e) =>
                              setEmailSettings((prev) => ({
                                ...prev,
                                mailgun_api_key: e.target.value,
                              }))
                            }
                            placeholder="key-xxxxxxxxxxxx"
                            className="w-full h-10 px-4 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('mailgun')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword['mailgun'] ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSaveEmail}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Saving...
                  </>
                ) : saveSuccess ? (
                  <>
                    <Check size={18} />
                    Saved
                  </>
                ) : (
                  'Save Email Settings'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Domain Tab */}
        {activeTab === 'domain' && (
          <div className="max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Domain Settings</h2>
            <p className="text-sm text-gray-500 mb-6">
              Configure your school's subdomain and custom domain.
            </p>

            {/* Current Subdomain */}
            <div className="p-4 bg-teal-50 rounded-lg border border-teal-200 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-teal-800">Your School URL</p>
                  <p className="text-lg font-semibold text-teal-900">
                    {domainSettings.subdomain}.{domainSettings.platform_domain}
                  </p>
                </div>
                <button
                  onClick={() =>
                    copyToClipboard(
                      `https://${domainSettings.subdomain}.${domainSettings.platform_domain}`,
                      'url'
                    )
                  }
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-teal-700 border border-teal-300 rounded-lg hover:bg-teal-100"
                >
                  {copiedField === 'url' ? (
                    <>
                      <Check size={16} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      Copy URL
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Subdomain Settings */}
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Subdomain</h3>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={domainSettings.subdomain}
                  onChange={(e) =>
                    setDomainSettings((prev) => ({
                      ...prev,
                      subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                    }))
                  }
                  className="w-48 h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
                <span className="text-gray-500">.{domainSettings.platform_domain}</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Only lowercase letters, numbers, and hyphens allowed
              </p>
            </div>

            {/* Custom Domain */}
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Custom Domain (Optional)</h3>
              <p className="text-sm text-gray-500 mb-4">
                Connect your own domain to use instead of the subdomain.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Domain
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={domainSettings.custom_domain}
                      onChange={(e) =>
                        setDomainSettings((prev) => ({
                          ...prev,
                          custom_domain: e.target.value,
                          custom_domain_verified: false,
                        }))
                      }
                      placeholder="school.yourdomain.com"
                      className="flex-1 h-10 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                    {domainSettings.custom_domain && (
                      <button
                        onClick={handleVerifyDomain}
                        disabled={verifyingDomain}
                        className="flex items-center gap-2 px-4 py-2 border border-teal-300 text-teal-700 rounded-lg hover:bg-teal-50 disabled:opacity-50"
                      >
                        {verifyingDomain ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : domainSettings.custom_domain_verified ? (
                          <CheckCircle size={16} className="text-green-500" />
                        ) : (
                          'Verify'
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* DNS Instructions */}
                {domainSettings.custom_domain && !domainSettings.custom_domain_verified && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex gap-3">
                      <Info size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium mb-2">DNS Configuration Required</p>
                        <p className="mb-3">
                          Add the following DNS record to your domain to connect it:
                        </p>

                        <div className="bg-white rounded-lg p-3 border border-amber-200 space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-500">Record Type</p>
                              <p className="font-mono font-semibold">A</p>
                            </div>
                            <button
                              onClick={() => copyToClipboard('A', 'type')}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              {copiedField === 'type' ? (
                                <Check size={14} className="text-green-500" />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-500">Host/Name</p>
                              <p className="font-mono font-semibold">
                                {domainSettings.custom_domain.split('.')[0] || '@'}
                              </p>
                            </div>
                            <button
                              onClick={() =>
                                copyToClipboard(
                                  domainSettings.custom_domain.split('.')[0] || '@',
                                  'host'
                                )
                              }
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              {copiedField === 'host' ? (
                                <Check size={14} className="text-green-500" />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-500">Points To / Value</p>
                              <p className="font-mono font-semibold text-teal-600">
                                {domainSettings.platform_ip}
                              </p>
                            </div>
                            <button
                              onClick={() => copyToClipboard(domainSettings.platform_ip, 'ip')}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              {copiedField === 'ip' ? (
                                <Check size={14} className="text-green-500" />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                          </div>

                          <div>
                            <p className="text-xs text-gray-500">TTL</p>
                            <p className="font-mono font-semibold">3600 (or Auto)</p>
                          </div>
                        </div>

                        <p className="mt-3 text-amber-700">
                          DNS changes can take up to 48 hours to propagate. Click "Verify" once
                          you've added the record.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Verified Status */}
                {domainSettings.custom_domain_verified && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-green-500" />
                      <div>
                        <p className="font-medium text-green-800">Domain Verified</p>
                        <p className="text-sm text-green-700">
                          Your custom domain is connected and working.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSaveDomain}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Saving...
                  </>
                ) : saveSuccess ? (
                  <>
                    <Check size={18} />
                    Saved
                  </>
                ) : (
                  'Save Domain Settings'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function IntegrationCard({
  integration,
  isConnected,
  onConnect,
}: {
  integration: Integration;
  isConnected: boolean;
  onConnect: () => void;
}) {
  return (
    <div
      className={`p-4 rounded-lg border ${isConnected ? 'border-teal-200 bg-teal-50' : 'border-gray-200'}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
          {integration.icon ? (
            <img src={integration.icon} alt={integration.name} className="w-6 h-6 object-contain" />
          ) : (
            <ExternalLink className="w-5 h-5 text-gray-400" />
          )}
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-gray-900">{integration.name}</h4>
          <p className="text-sm text-gray-500">{integration.description}</p>
        </div>
      </div>
      <button
        onClick={onConnect}
        className={`mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          isConnected
            ? 'bg-teal-600 text-white hover:bg-teal-700'
            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
      >
        {isConnected ? (
          <>
            <Check className="w-4 h-4" />
            Connected
          </>
        ) : (
          'Connect'
        )}
      </button>
    </div>
  );
}
